import { FAQLink, FAQTopic } from '@components/FAQ';
import { EXTERNAL_LINKS } from '@constants/externalLinks';

import { Sections, Topics } from './constants';

const BROADBAND_TOPICS: FAQTopic<Topics>[] = [
  {
    id: Topics.downloadAndUploadSpeeds,
    question: 'What are upload and download speeds?',
    answer: (
      <>
        Download speed is the speed at which your internet connection can
        retrieve data from the internet. Upload speed is the speed that your
        internet connection can allow data to be sent from your devices to the
        internet.
      </>
    ),
  },
  {
    id: Topics.needForSpeed,
    question: 'What speeds does my home need?',
    answer: (
      <>
        If you already have broadband, you'll likely know if its speeds meet
        your needs. Googling 'speed test' on a device that's connected to
        broadband will reveal its speeds.
        <br />
        <br />
        Here's a general guide for download speeds:
        <br />
        <br />
        &#8226; Up to 15 Mbps: Basic online activities like browsing, emails,
        and low-resolution video. <br />
        &#8226; 15-50 Mbps: HD video streaming and managing a few smart devices.{' '}
        <br />
        &#8226; 50-100 Mbps: 4K video streaming on one or two devices, online
        gaming, and supporting a few smart devices. <br />
        &#8226; 100 Mbps or more: 4K streaming on multiple devices and
        supporting numerous smart devices.
      </>
    ),
  },
  {
    id: Topics.workOutAverageSpeed,
    question: 'How do you work out my average broadband speeds?',
    answer: (
      <>
        We work out speeds using your address and/or landline. The speeds
        available are estimated speeds between your wireless router and the
        internet during peak times (8pm – 10pm). We calculate your estimates by
        looking at the speeds of customers that have a line similar length to
        yours, and account for any congestion experienced at peak times. Your
        actual speeds may be lower and your 'minimum download speed' is the
        absolute minimum download speed that you can expect to receive on your
        broadband line at peak times. If your broadband speed drops below the
        quoted 'minimum download speed' and we can't fix it within 30 calendar
        days of you raising the issue with us, you have the right to end the
        contract without paying the early termination fee. Where we can't get
        any information about the telephone line or address, we'll quote 'up to'
        speeds.
      </>
    ),
  },
  {
    id: Topics.speedNotFastEnough,
    question: 'What if the speeds I end up getting aren’t fast enough?',
    answer: (
      <>
        You can upgrade onto a higher speed at any time in your contract, though
        you'll need to pay the difference in price. Customers can upgrade their
        broadband speed by calling in.
      </>
    ),
  },
  {
    id: Topics.supportWithBroadband,
    question: 'What support do you offer if I have problems with my broadband?',
    answer: (
      <>
        We offer an in-app troubleshooting tool to help you fix any problems
        with your broadband. If you can't resolve the problem on your own, you
        can call our customer support, who will run diagnostics remotely and
        help you find a solution.
      </>
    ),
  },
];

const SWITCHING_TOPICS: FAQTopic<Topics>[] = [
  {
    id: Topics.whenSwitchGoLive,
    question: 'When will my broadband go live?',
    answer: (
      <>
        UW broadband will typically go live within 14 working days of ordering
        it. The end-to-end process isn't entirely in our control, so it's hard
        to guarantee a timeframe, but we will keep you informed every step of
        the way.
      </>
    ),
  },
  {
    id: Topics.needToContactOldProvider,
    question:
      'Do I need to contact my current broadband supplier when I switch?',
    answer: (
      <>
        Normally we can let your current provider know you’re switching to UW.
        <br />
        <FAQLink href={EXTERNAL_LINKS.contactOldBBProviderHelp}>
          In some cases
        </FAQLink>
        , you will need to contact your old provider to let them know you're
        switching.
      </>
    ),
  },
  {
    id: Topics.routerDelivery,
    question: 'When will my router be delivered?',
    answer: (
      <>
        Our customers typically receive their WiFi equipment within 10 working
        days (before their broadband service goes live).
      </>
    ),
  },
  {
    id: Topics.engineerVisit,
    question: 'Will an engineer need to visit my home?',
    answer: (
      <>
        You may need an engineer visit to get your service live. We'll let you
        know if so, and will book you into the earliest slot available. If you
        can't make this time, it's easy to reschedule.
      </>
    ),
  },
  {
    id: Topics.exitFees,
    question: 'Will you pay my exit fees from my current supplier?',
    answer: (
      <>
        We'll reimburse up to £400 if you incur early termination fees when
        switching your services to UW.{' '}
        <FAQLink href={EXTERNAL_LINKS.earlyTerminationRefundHelp}>
          Eligibility and terms apply
        </FAQLink>
        .
      </>
    ),
  },
];

const LANDLINE_TOPICS: FAQTopic<Topics>[] = [
  {
    id: Topics.landlineSwitch,
    question: 'What happens to my landline when I switch?',
    answer: (
      <>
        It depends on the broadband tariff you choose. If you take our Ultra,
        Ultra+, or Standard broadband tariffs, your existing landline will
        switch over to us as part of the service. If you take one of our Full
        Fibre tariffs (Fibre 40, Fibre 80, Full Fibre 150, Full Fibre 500, or
        Full Fibre 900), your landline will move to our Digital Home Phone
        service, which works over your broadband connection. You’ll keep your
        existing phone number and can continue making and receiving calls as
        normal. <b>Please note:</b> Digital Home Phone requires your broadband
        connection to work, so it will not function during a power cut or
        broadband outage.  You’ll also need a mobile to call 999 or 112 in an
        emergency if your broadband is down.
      </>
    ),
  },
  {
    id: Topics.landlineCallPackage,
    question: 'Will my landline still work without a call package?',
    answer: (
      <>
        Yes, you can still make and receive calls without a call package.{' '}
        <FAQLink href={EXTERNAL_LINKS.chargesApply}>Costs apply</FAQLink>.
      </>
    ),
  },
];

const faqSections = [
  {
    id: Sections.speeds,
    title: 'Broadband speeds',
    topics: BROADBAND_TOPICS,
  },
  {
    id: Sections.switching,
    title: 'Switching',
    topics: SWITCHING_TOPICS,
  },
  {
    id: Sections.landline,
    title: 'Landline',
    topics: LANDLINE_TOPICS,
  },
];

export { faqSections };
